import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Receipt,
  RefreshCw,
  Save,
  Search,
  Tv,
  Zap,
  GraduationCap,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminBillsService, getApiErrorMessage } from '@/services';
import type { BillPlan, BillProduct } from '@/types/bills';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { notify } from '@/utils/notify';

type BillCategory = 'electricity' | 'cable_tv' | 'exam';

const CATEGORY_META: Record<
  BillCategory,
  { label: string; icon: typeof Zap; hint: string }
> = {
  electricity: {
    label: 'Electricity',
    icon: Zap,
    hint: 'VTpass commission % per disco. Caps limit max commission per transaction.',
  },
  cable_tv: {
    label: 'Cable TV',
    icon: Tv,
    hint: 'Set role discounts per provider. Bouquet prices sync from VTpass.',
  },
  exam: {
    label: 'Exams',
    icon: GraduationCap,
    hint: 'Fixed ₦ commission from VTpass. Agent rebate is your margin share.',
  },
};

const SAMPLE_AMOUNT = 5000;

interface PriceDraft {
  providerDiscount: string;
  subscriberDiscount: string;
  agentDiscount: string;
  providerCommissionCap: string;
  agentCommissionCap: string;
  providerCommissionFixed: string;
  agentCommissionFixed: string;
}

function toDraft(product: BillProduct): PriceDraft {
  return {
    providerDiscount: String(product.providerDiscount),
    subscriberDiscount: String(product.subscriberDiscount),
    agentDiscount: String(product.agentDiscount),
    providerCommissionCap:
      product.providerCommissionCap != null ? String(product.providerCommissionCap) : '',
    agentCommissionCap: product.agentCommissionCap != null ? String(product.agentCommissionCap) : '',
    providerCommissionFixed:
      product.providerCommissionFixed != null ? String(product.providerCommissionFixed) : '',
    agentCommissionFixed:
      product.agentCommissionFixed != null ? String(product.agentCommissionFixed) : '',
  };
}

function parseField(value: string): number | null {
  if (value.trim() === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function parsePercent(value: string): number | null {
  const num = parseField(value);
  if (num == null || num < 0 || num > 100) return null;
  return num;
}

function isDraftDirty(product: BillProduct, draft: PriceDraft): boolean {
  const saved = toDraft(product);
  return (Object.keys(saved) as (keyof PriceDraft)[]).some((key) => saved[key] !== draft[key]);
}

function previewPercentProfit(
  faceValue: number,
  providerRate: number,
  roleRate: number,
  providerCap: number | null,
  agentCap: number | null,
  userType: 'subscriber' | 'agent',
): number {
  const rawProvider = (faceValue * providerRate) / 100;
  const providerCommission =
    providerCap != null && providerCap > 0 ? Math.min(rawProvider, providerCap) : rawProvider;
  const cost = faceValue - providerCommission;
  const rawRole = (faceValue * roleRate) / 100;
  const roleRebate =
    userType === 'agent' && agentCap != null && agentCap > 0
      ? Math.min(rawRole, agentCap)
      : rawRole;
  const sell = faceValue - roleRebate;
  return Math.round((sell - cost) * 100) / 100;
}

function previewFixedProfit(
  faceValue: number,
  providerFixed: number,
  agentFixed: number,
  userType: 'subscriber' | 'agent',
): number {
  const providerCut = Math.min(faceValue, Math.max(0, providerFixed));
  const roleCut = userType === 'agent' ? Math.min(providerCut, Math.max(0, agentFixed)) : 0;
  const cost = faceValue - providerCut;
  const sell = faceValue - roleCut;
  return Math.round((sell - cost) * 100) / 100;
}

function formatProfit(value: number | null) {
  if (value == null) return '—';
  return `₦${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function AdminBillPricingPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [category, setCategory] = useState<BillCategory>('electricity');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<BillProduct[]>([]);
  const [drafts, setDrafts] = useState<Record<number, PriceDraft>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [plansByProduct, setPlansByProduct] = useState<Record<number, BillPlan[]>>({});
  const [plansLoadingId, setPlansLoadingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const needsPlans = category === 'cable_tv' || category === 'exam';
  const meta = CATEGORY_META[category];

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminBillsService.listProducts(category);
      const list = res.data?.products ?? [];
      setProducts(list);
      setDrafts(Object.fromEntries(list.map((p) => [p.id, toDraft(p)])));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load bill products'));
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void loadProducts();
    setExpandedId(null);
    setSearch('');
  }, [loadProducts]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.fullName.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.serviceId.toLowerCase().includes(q),
    );
  }, [products, search]);

  const categoryCounts = useMemo(
    () => ({
      electricity: category === 'electricity' ? products.length : '—',
      cable_tv: category === 'cable_tv' ? products.length : '—',
      exam: category === 'exam' ? products.length : '—',
    }),
    [category, products.length],
  );

  function updateDraft(productId: number, patch: Partial<PriceDraft>) {
    setDrafts((prev) => ({
      ...prev,
      [productId]: { ...prev[productId]!, ...patch },
    }));
  }

  async function handleSave(product: BillProduct) {
    const draft = drafts[product.id];
    if (!draft) return;

    const providerDiscount = parsePercent(draft.providerDiscount);
    const subscriberDiscount = parsePercent(draft.subscriberDiscount);
    const agentDiscount = parsePercent(draft.agentDiscount);

    if (
      product.commissionMode === 'percent' &&
      (providerDiscount == null || subscriberDiscount == null || agentDiscount == null)
    ) {
      notify.error('Enter valid percentages (0–100) for all discount fields');
      return;
    }

    if (
      product.commissionMode === 'percent' &&
      (subscriberDiscount! > providerDiscount! || agentDiscount! > providerDiscount!)
    ) {
      notify.error('Subscriber and agent discounts cannot exceed provider discount');
      return;
    }

    setSavingId(product.id);
    try {
      const res = await adminBillsService.updateProduct(product.id, {
        providerDiscount: providerDiscount ?? undefined,
        subscriberDiscount: subscriberDiscount ?? undefined,
        agentDiscount: agentDiscount ?? undefined,
        providerCommissionCap: parseField(draft.providerCommissionCap),
        agentCommissionCap: parseField(draft.agentCommissionCap),
        providerCommissionFixed: parseField(draft.providerCommissionFixed) ?? undefined,
        agentCommissionFixed: parseField(draft.agentCommissionFixed) ?? undefined,
      });
      if (res.data?.product) {
        const updated = res.data.product;
        setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
        setDrafts((prev) => ({ ...prev, [product.id]: toDraft(updated) }));
        notify.success(`${product.name} pricing saved`);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to save'));
    } finally {
      setSavingId(null);
    }
  }

  async function loadPlans(productId: number) {
    setPlansLoadingId(productId);
    try {
      const res = await adminBillsService.listPlans(productId);
      setPlansByProduct((prev) => ({ ...prev, [productId]: res.data?.plans ?? [] }));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load plans'));
    } finally {
      setPlansLoadingId(null);
    }
  }

  async function handleSyncPlans(product: BillProduct) {
    setSyncingId(product.id);
    try {
      const res = await adminBillsService.syncPlans(product.id);
      setPlansByProduct((prev) => ({ ...prev, [product.id]: res.data?.plans ?? [] }));
      setExpandedId(product.id);
      notify.success(`${product.name} plans synced from VTpass`);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Sync failed'));
    } finally {
      setSyncingId(null);
    }
  }

  async function togglePlans(product: BillProduct) {
    if (expandedId === product.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(product.id);
    if (!plansByProduct[product.id]) {
      await loadPlans(product.id);
    }
  }

  const inputClass = cn(
    'w-full min-w-0 rounded-lg border px-2.5 py-2 text-sm focus:outline-none focus:ring-2',
    shell.input,
  );

  const isPercentMode = products[0]?.commissionMode !== 'fixed' || category !== 'exam';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading bill pricing" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">Bill Pricing</h1>
        <p className={cn('mt-1 text-sm sm:text-base', shell.cardMuted)}>
          Configure VTpass commission rates and your subscriber/agent margins for electricity, TV, and
          exam products.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {(Object.keys(CATEGORY_META) as BillCategory[]).map((cat) => {
          const item = CATEGORY_META[cat];
          const Icon = item.icon;
          const active = category === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={cn(
                'rounded-xl border px-4 py-4 text-left transition',
                active ? 'border-secondary bg-secondary/10 ring-1 ring-secondary/30' : shell.card,
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className={cn('h-4 w-4', active ? 'text-secondary' : shell.cardMuted)} />
                <span className="text-xs font-semibold uppercase tracking-wide">{item.label}</span>
              </div>
              <p className="mt-2 text-2xl font-bold">
                {active ? products.length : categoryCounts[cat]}
              </p>
              <p className={cn('mt-1 text-xs leading-relaxed', shell.cardMuted)}>{item.hint}</p>
            </button>
          );
        })}
      </div>

      <Card className={cn('p-4 sm:p-5', shell.card)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1">
            <Search className={cn('absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', shell.cardMuted)} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${meta.label.toLowerCase()} providers...`}
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <p className={cn('text-sm', shell.cardMuted)}>
            {filteredProducts.length} of {products.length} providers
          </p>
        </div>
      </Card>

      {filteredProducts.length === 0 ? (
        <Card className={cn('p-10 text-center', shell.card)}>
          <Receipt className={cn('mx-auto h-10 w-10', shell.cardMuted)} />
          <p className={cn('mt-3', shell.cardMuted)}>No providers match your search.</p>
        </Card>
      ) : category === 'electricity' || (category === 'exam' && !isPercentMode) ? (
        <Card className={cn('overflow-hidden', shell.card)}>
          <div className={cn('border-b px-4 py-4 sm:px-6', shell.tableWrap)}>
            <h2 className="text-lg font-bold">{meta.label} providers</h2>
            <p className={cn('mt-1 text-sm', shell.cardMuted)}>
              Sample profit on ₦{SAMPLE_AMOUNT.toLocaleString()} transaction
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={cn('text-left text-xs uppercase tracking-wide', shell.tableHead)}>
                <tr>
                  <th className="px-4 py-3 sm:px-6">Provider</th>
                  {category === 'electricity' ? (
                    <>
                      <th className="px-4 py-3">Provider %</th>
                      <th className="px-4 py-3">Subscriber %</th>
                      <th className="px-4 py-3">Agent %</th>
                      <th className="px-4 py-3">Prov. cap</th>
                      <th className="px-4 py-3">Agent cap</th>
                      <th className="px-4 py-3">Profit preview</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3">Provider ₦</th>
                      <th className="px-4 py-3">Agent rebate ₦</th>
                      <th className="px-4 py-3">Profit preview</th>
                    </>
                  )}
                  <th className="px-4 py-3 sm:pr-6" />
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const draft = drafts[product.id] ?? toDraft(product);
                  const dirty = isDraftDirty(product, draft);
                  const isFixed = product.commissionMode === 'fixed';

                  let subProfit: number | null = null;
                  let agentProfit: number | null = null;

                  if (isFixed) {
                    const pf = parseField(draft.providerCommissionFixed);
                    const af = parseField(draft.agentCommissionFixed);
                    if (pf != null && af != null) {
                      subProfit = previewFixedProfit(SAMPLE_AMOUNT, pf, af, 'subscriber');
                      agentProfit = previewFixedProfit(SAMPLE_AMOUNT, pf, af, 'agent');
                    }
                  } else {
                    const pd = parsePercent(draft.providerDiscount);
                    const sd = parsePercent(draft.subscriberDiscount);
                    const ad = parsePercent(draft.agentDiscount);
                    const pc = parseField(draft.providerCommissionCap);
                    const ac = parseField(draft.agentCommissionCap);
                    if (pd != null && sd != null && ad != null) {
                      subProfit = previewPercentProfit(SAMPLE_AMOUNT, pd, sd, pc, ac, 'subscriber');
                      agentProfit = previewPercentProfit(SAMPLE_AMOUNT, pd, ad, pc, ac, 'agent');
                    }
                  }

                  return (
                    <tr
                      key={product.id}
                      className={cn('border-t', shell.tableRow, dirty && 'bg-secondary/5')}
                    >
                      <td className="px-4 py-3 sm:px-6">
                        <p className="font-medium">{product.fullName}</p>
                        <p className={cn('font-mono text-xs', shell.cardMuted)}>{product.serviceId}</p>
                      </td>

                      {isFixed ? (
                        <>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.providerCommissionFixed}
                              onChange={(e) =>
                                updateDraft(product.id, { providerCommissionFixed: e.target.value })
                              }
                              className={cn(inputClass, 'max-w-[100px]')}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.agentCommissionFixed}
                              onChange={(e) =>
                                updateDraft(product.id, { agentCommissionFixed: e.target.value })
                              }
                              className={cn(inputClass, 'max-w-[100px]')}
                            />
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div>Sub: {formatProfit(subProfit)}</div>
                            <div className={shell.cardMuted}>Ag: {formatProfit(agentProfit)}</div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.providerDiscount}
                              onChange={(e) =>
                                updateDraft(product.id, { providerDiscount: e.target.value })
                              }
                              className={cn(inputClass, 'max-w-[80px]')}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.subscriberDiscount}
                              onChange={(e) =>
                                updateDraft(product.id, { subscriberDiscount: e.target.value })
                              }
                              className={cn(inputClass, 'max-w-[80px]')}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.agentDiscount}
                              onChange={(e) =>
                                updateDraft(product.id, { agentDiscount: e.target.value })
                              }
                              className={cn(inputClass, 'max-w-[80px]')}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.providerCommissionCap}
                              onChange={(e) =>
                                updateDraft(product.id, { providerCommissionCap: e.target.value })
                              }
                              placeholder="—"
                              className={cn(inputClass, 'max-w-[90px]')}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={draft.agentCommissionCap}
                              onChange={(e) =>
                                updateDraft(product.id, { agentCommissionCap: e.target.value })
                              }
                              placeholder="—"
                              className={cn(inputClass, 'max-w-[90px]')}
                            />
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div>Sub: {formatProfit(subProfit)}</div>
                            <div className={shell.cardMuted}>Ag: {formatProfit(agentProfit)}</div>
                          </td>
                        </>
                      )}

                      <td className="px-4 py-3 sm:pr-6">
                        <Button
                          size="sm"
                          variant={dirty ? 'primary' : 'outline'}
                          className={!dirty ? shell.outlineBtn : undefined}
                          loading={savingId === product.id}
                          loadingText="Saving"
                          onClick={() => void handleSave(product)}
                        >
                          <Save className="h-3.5 w-3.5" />
                          Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((product) => {
            const draft = drafts[product.id] ?? toDraft(product);
            const dirty = isDraftDirty(product, draft);
            const expanded = expandedId === product.id;
            const plans = plansByProduct[product.id] ?? [];
            const isFixed = product.commissionMode === 'fixed';

            const pd = parsePercent(draft.providerDiscount);
            const sd = parsePercent(draft.subscriberDiscount);
            const ad = parsePercent(draft.agentDiscount);
            const pc = parseField(draft.providerCommissionCap);
            const ac = parseField(draft.agentCommissionCap);
            const subProfit =
              pd != null && sd != null
                ? previewPercentProfit(SAMPLE_AMOUNT, pd, sd, pc, ac, 'subscriber')
                : null;
            const agentProfit =
              pd != null && ad != null
                ? previewPercentProfit(SAMPLE_AMOUNT, pd, ad, pc, ac, 'agent')
                : null;

            return (
              <Card key={product.id} className={cn('overflow-hidden', shell.card)}>
                <div
                  className={cn(
                    'flex flex-wrap items-center justify-between gap-4 border-b px-4 py-4 sm:px-6',
                    shell.tableWrap,
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary/15">
                      <Tv className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-lg font-bold">{product.fullName}</h2>
                        {dirty && (
                          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
                            Unsaved
                          </span>
                        )}
                      </div>
                      <p className={cn('text-sm', shell.cardMuted)}>
                        {product.serviceId}
                        {plans.length > 0 && ` · ${plans.length} plans`}
                        {product.supportsRenewal && ' · Renewal supported'}
                        {product.usesPhoneAsBiller && ' · Phone as biller'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {needsPlans && (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={shell.outlineBtn}
                          loading={syncingId === product.id}
                          onClick={() => void handleSyncPlans(product)}
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          Sync VTpass
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className={shell.outlineBtn}
                          onClick={() => void togglePlans(product)}
                        >
                          {expanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                          Plans
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      loading={savingId === product.id}
                      loadingText="Saving"
                      onClick={() => void handleSave(product)}
                    >
                      <Save className="h-3.5 w-3.5" />
                      Save
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto p-4 sm:p-6">
                  {!isFixed ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
                      {(
                        [
                          ['Provider %', 'providerDiscount', draft.providerDiscount],
                          ['Subscriber %', 'subscriberDiscount', draft.subscriberDiscount],
                          ['Agent %', 'agentDiscount', draft.agentDiscount],
                          ['Provider cap ₦', 'providerCommissionCap', draft.providerCommissionCap],
                          ['Agent cap ₦', 'agentCommissionCap', draft.agentCommissionCap],
                        ] as const
                      ).map(([label, field, value]) => (
                        <label key={field} className="block text-sm">
                          <span className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                            {label}
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={value}
                            onChange={(e) => updateDraft(product.id, { [field]: e.target.value })}
                            placeholder={field.includes('Cap') ? 'Optional' : undefined}
                            className={inputClass}
                          />
                        </label>
                      ))}
                      <div className="block text-sm">
                        <span className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                          Profit on ₦{SAMPLE_AMOUNT.toLocaleString()}
                        </span>
                        <div
                          className={cn(
                            'rounded-lg border px-3 py-2.5 text-xs',
                            shell.tableWrap,
                            shell.tableCell,
                          )}
                        >
                          <div>Sub: {formatProfit(subProfit)}</div>
                          <div className={shell.cardMuted}>Ag: {formatProfit(agentProfit)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-4 sm:grid-cols-3">
                      <label className="block text-sm">
                        <span className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                          Provider commission ₦
                        </span>
                        <input
                          type="text"
                          value={draft.providerCommissionFixed}
                          onChange={(e) =>
                            updateDraft(product.id, { providerCommissionFixed: e.target.value })
                          }
                          className={inputClass}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                          Agent rebate ₦
                        </span>
                        <input
                          type="text"
                          value={draft.agentCommissionFixed}
                          onChange={(e) =>
                            updateDraft(product.id, { agentCommissionFixed: e.target.value })
                          }
                          className={inputClass}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {expanded && (
                  <div className={cn('border-t px-4 py-4 sm:px-6', shell.tableWrap)}>
                    {plansLoadingId === product.id ? (
                      <Loader label="Loading plans..." />
                    ) : plans.length === 0 ? (
                      <p className={cn('text-sm', shell.cardMuted)}>
                        No plans loaded. Click &quot;Sync VTpass&quot; to fetch bouquets from sandbox.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className={cn('text-left text-xs uppercase tracking-wide', shell.cardMuted)}>
                            <tr>
                              <th className="pb-2 pr-4">Bouquet / plan</th>
                              <th className="pb-2 pr-4">Code</th>
                              <th className="pb-2 pr-4">Face value</th>
                              <th className="pb-2 pr-4">Subscriber</th>
                              <th className="pb-2">Agent</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plans.map((plan) => (
                              <tr key={plan.id} className={cn('border-t', shell.tableRow)}>
                                <td className="py-2.5 pr-4 font-medium">{plan.name}</td>
                                <td className={cn('py-2.5 pr-4 font-mono text-xs', shell.cardMuted)}>
                                  {plan.variationCode}
                                </td>
                                <td className="py-2.5 pr-4">₦{plan.faceValue.toLocaleString()}</td>
                                <td className="py-2.5 pr-4">₦{plan.subscriberPrice.toLocaleString()}</td>
                                <td className="py-2.5">₦{plan.agentPrice.toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
